/**
 * SideMenu
 */

// React
import React, { useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';

// UI
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import {
  ListItem,
  Collapse,
  ListItemIcon,
  ListItemText,
  Drawer,
  List,
  Avatar,
  DialogTitle,
  DialogActions,
  Dialog,
  Button,
  IconButton,
  Backdrop,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronUp, mdiMenu } from '@mdi/js';
// Component
import Can from '../../components/Can';
import AuthContext from '../../authContext';

// Type
import { MenuInterface, Menus } from './interfaces';
import MenuList from './menus';
import windowSize from '../../utils/hook/useWindowResize';
// import sass from './sideBar.module.scss';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    icon: {
      color: 'inherit',
    },
    iconActive: {
      '& svg': {
        color: "#FFFFFF",
      },
    },
    toggleHolder: {
      height: '129px',
    },

    hideList: {
      display: 'none',
    },
    activePro: {
      '&:hover': {
        backgroundColor: '#24388D !important',
        textDecorationColor: '#FFFFFF',
        color: '#FFFFFF',
      },
      // textDecorationColor: '#FFFFFF',
      color: '#FFFFFF',
      backgroundColor: '#24388D',
      // backgroundColor: 'rgba(37, 54, 140, 0.2)',
    },
    item: {
      color: 'inherit',
      textDecoration: 'none',
    },
    sidebar: {
      '@media (max-width: 768px)': {
        position: 'fixed',
        transform: 'translate(-100%, -100%)',
        zIndex: '9',
      },
    },
    sidebarOpenMob: {
      '@media (max-width: 768px)': {
        transform: 'none',
      },
    },
    sidebarCloseMob: {
      '@media (max-width: 768px)': {
        transform: 'translate(-100%, -100%)',
      },
    },
    drawer: {
      marginTop: '40px',
      marginBottom: '70px',
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
    drawerOpen: {
      width: drawerWidth,
      overflow: 'visible',
      background: '#ffffff',
      color: '#313131',
      justifyContent: "space-between"
    },
    drawerClose: {
      marginBottom: '7px',
      overflowX: 'hidden',
      width: theme.spacing(8) + 1,
      justifyContent: "space-between",
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9) + 1,
      },
      background: '#ffffff',
      color: '#313131',
      '@media (max-width: 480px)': {
        display: 'none',
      },
    },
    menuButton: {
      marginRight: 36,
    },
    hide: {
      display: 'none',
    },

    nested: {
      '& > span': {
        paddingLeft: theme.spacing(7),
        fontSize: '14px !important',
      },
    },
    avatar: {
      margin: theme.spacing(6, 'auto', 7),
      height: '70px !important',
    },
    avatarLarg: {
      width: '221px',
      height: theme.spacing(15),
    },

    avatarSmall: {
      width: theme.spacing(5),
      height: theme.spacing(5),
    },
    avatarMob: {
      width: theme.spacing(0),
      height: theme.spacing(0),
    },
    userInfoContainer: {
      // marginTop: 'auto',
      alignItems: 'center',
    },
    logoutBar: {
      display: 'flex',
      justifyItems: 'center',
      alignItems: 'center',
      padding: '24px',
    },
    logoutProfileContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    userInfoBox: {
      display: 'flex',
      alignItems: 'center',
      padding: '2px 16px',
      marginRight: '5px',
      marginTop: '64px',
    },
    userInfoIconBox: {
      backgroundColor: '#B9C4E1',
      display: 'flex',
    },
    userAvatar: {
      backgroundColor: '#B9C4E1',
      width: '43px',
      height: '43px',
    },
    logoutIcon: {
      width: '24px',
      height: '24px',
    },
    username: {
      padding: theme.spacing(0, 1),
      fontSize: '16px',
      fontWeight: 500,
      color: "#000 !important",
    },
    toolbar: {
      display: 'flex',
      position: 'relative',
      top: 0,
      // backgroundColor: '#fff',
      zIndex: 20,

      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
    },
    toolbarOpen: {
      width: drawerWidth,
      // alignItems: 'flex-start',
      transition: theme.transitions.create(['all'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    toolbarClose: {
      width: '73px',
      transition: theme.transitions.create(['all'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },

    divider: {
      backgroundColor: '#fff',
      margin: '0 16px',
    },
    mobMenu: {
      display: 'none',
      '@media (max-width: 480px)': {
        position: 'fixed',
        top: '0',
        right: '0',
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '64px',
        backgroundColor: '#fff',
        padding: '20px',
        zIndex: '10',
        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.08)',
      },
    },
    vitrinLink: {
      borderTop: "1px solid #FFFFFF5E",
      borderBottom: "1px solid #FFFFFF5E"
    },
    vitrinIcon: {
      width: "1.5rem",
      height: "1.5rem",
    }
  })
);

// Const
const drawerWidth = 245;
const vitrin = localStorage.getItem('vitrin') || null;

export default function SideMenu(props: any) {
  const classes = useStyles();
  const authContext = useContext(AuthContext);
  const [open, setOpen] = React.useState(false);
  const [openConfirmLogoutBox, setOpenConfirmLogoutBox] = React.useState(false);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [isMob, setIsMob] = React.useState(false);
  const [openList, setOpenList] = React.useState(0);
  let menu = [...MenuList]
  const { width } = windowSize();

  useEffect(() => {
    if (width && width <= 480) {
      setIsMob(true);
    } else {
      setIsMob(false);
    }
  }, [width]);

  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setOpenList(0);
  };

  const handleDrawerOver = () => {
    if (!isMob) setOpenDrawer(true);
  };
  const handleDrawerLeave = () => {
    if (!isMob) {
      setOpenDrawer(false);
      setOpen(false);
    }
  };

  const handleDrawerMobOpen = () => {
    setOpenDrawer(true);
  };

  const handleCollapse = (e: any, menu: any) => {
    menu.onClick && handleLogout(e);
    setOpenList(0);
    setOpen(false);
    setOpenDrawer(false);
  };

  const handleClick = (id: number) => {
    setOpen(!open);
    openList === id ? setOpenList(0) : setOpenList(id);
  };

  const getActiveSubMenu = (menu: any): boolean => {
    return (
      menu?.subMenu?.filter((menu: any) => {
        return window.location.pathname.includes(menu.path);
      }).length !== 0
    );
  };

  const handleOpenConfirmLogoutBox = () => {
    setOpenConfirmLogoutBox(false);
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('partnerId');
    localStorage.removeItem('warehouseId');
    localStorage.removeItem('userId');
    localStorage.removeItem('name');
    localStorage.removeItem('vitrin')
    window.location.href = '/login';
  };

  const handleLogout = (e: React.MouseEvent<unknown>) => {
    e.preventDefault();
    setOpenConfirmLogoutBox(true);
  };

  const links = (
    <List>
      {/* <Can
        role={authContext.user.role}
        key="234"
        perform="/vitrin/categories"
        yes={() => (
          <Link
            to="/vitrin/categories"
            key="234"
            className={classes.item}
            style={{
              padding: "0 10px"
            }}
          >
            <ListItem
              button
              // alignItems={'center'}
              className={classes.vitrinLink}
              style={
                !openDrawer
                  ? {
                    paddingTop: '12px',
                    paddingBottom: '12px',
                  }
                  : {}
              }
              data-cy="vitrinLink"
            >
              <ListItemIcon
                // className={!openDrawer ? classes.activePro : ''}
                // className={classes.vitrinLink}
                style={
                  !openDrawer
                    ? {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: '40px',
                      paddingLeft: '2px',
                      paddingRight: '2px',
                      paddingTop: '2px',
                      paddingBottom: '2px',
                      // borderRadius: '4px',
                    }
                    : {}
                }
              >
                <Avatar
                  variant="rounded"
                  className={classes.vitrinIcon}
                  src="/vitrin.svg"
                  sizes="sm"
                />
              </ListItemIcon>
              <ListItemText
                className={!openDrawer ? classes.hideList : " "}
                style={{
                  padding: "10px 0"
                }}
                primary="ویترین"
              />
            </ListItem>
          </Link>
        )}
      /> */}
      {menu.map((menu: MenuInterface, index: number) => {
        let activePro = '';
        if (
          menu.path === window.location.pathname ||
          window.location.pathname.includes(menu.path) ||
          (menu?.subMenu && getActiveSubMenu(menu))
        ) {
          activePro = `${classes.activePro} ${classes.iconActive}`;
        }
        const list = (
          <Can
            role={authContext.user.role}
            key={`${menu.id}_${index}`}
            perform={menu.path}
            yes={() => (
              <Link
                to={menu.path}
                key={`${menu.id}_${index}`}
                className={classes.item + (menu?.disabled ? ' disabled-link' : ' ')}
                onClick={e => handleCollapse(e, menu)}
              >
                <ListItem
                  button
                  // alignItems={'center'}
                  disableGutters
                  className={openDrawer ? activePro : ''}
                  style={{padding: "8px 5px"}}
                  data-cy={menu?.dataCy}
                  disabled={menu?.disabled}
                >
                  <ListItemIcon
                    className={!openDrawer ? activePro : ''}
                    style={
                      !openDrawer
                        ? {
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          minWidth: '40px',
                          paddingLeft: '2px',
                          paddingRight: '2px',
                          paddingTop: '6px',
                          paddingBottom: '6px',
                          borderRadius: '4px',
                          width: "100%"
                        }
                        : {}
                    }
                  >
                    <Icon path={menu.icon} className={classes.icon} size={1} />
                  </ListItemIcon>
                  <ListItemText
                    className={!openDrawer ? classes.hideList : ''}
                    primary={menu.sideMenuName}
                  />
                </ListItem>
              </Link>
            )}
          />
        );
        const subList = (
          <div key={`${menu.id}_${index}`} style={{marginBottom: "8px"}}>
            <Can
              role={authContext.user.role}
              perform={menu.path}
              key={`${menu.id}_${index}`}
              yes={() => (
                <ListItem
                  className={openDrawer ? activePro : ''}
                  button
                  disableGutters
                  style={{padding: "8px 5px"}}
                  key={`${menu.id}_${index}`}
                  onClick={() => handleClick(menu.id)}
                  disabled={menu?.disabled}
                >
                  <ListItemIcon
                    className={!openDrawer ? activePro : ''}
                    style={
                      !openDrawer
                        ? {
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          minWidth: '40px',
                          paddingLeft: '2px',
                          paddingRight: '2px',
                          paddingTop: '6px',
                          paddingBottom: '6px',
                          borderRadius: '4px',
                          width: "100%"
                        }
                        : {}
                    }
                  >
                    {menu.id == 234 ? <Avatar
                      variant="rounded"
                      className={classes.vitrinIcon}
                      src={menu.icon}
                      sizes="sm"
                    /> : <Icon path={menu.icon} className={classes.icon} size={1} />}
                  </ListItemIcon>
                  <ListItemText
                    style={{ fontSize: '4px' }}
                    className={!openDrawer ? classes.hideList : ''}
                    primary={menu.sideMenuName}
                  />
                  {openList === menu.id ? (
                    <Icon path={mdiChevronUp} size={1} />
                  ) : (
                    <Icon path={mdiChevronDown} size={1} />
                  )}
                </ListItem>
              )}
            />
            {
              <Collapse
                in={openList === menu.id && openDrawer}
                timeout="auto"
                unmountOnExit
                key={index}
              >
                <List component="div" disablePadding>
                  {menu.subMenu &&
                    menu.subMenu.map((menu, index) => {
                      let activePro = '';
                      if (menu.path === window.location.pathname) {
                        activePro = `${classes.activePro} `;
                      }

                      return (
                        <Can
                          role={authContext.user.role}
                          perform={menu.path}
                          key={`${menu.id}_${index}`}
                          yes={() => (
                            <Link
                              to={menu.path}
                              className={classes.item}
                              key={`${menu.id}_${index}`}
                              onClick={handleDrawerClose}
                            >
                              <ListItem
                                button
                                className={activePro + classes.nested}
                              >
                                <ListItemText
                                  className={
                                    !openDrawer
                                      ? classes.hideList
                                      : classes.nested
                                  }
                                  primary={menu.sideMenuName}
                                />
                              </ListItem>
                            </Link>
                          )}
                        />
                      );
                    })}
                </List>
              </Collapse>
            }
          </div>
        );

        if (!menu.subMenu) {
          return list;
        } else {
          return subList;
        }
      })}
    </List>
  );

  return (
    <div
      style={{
        position: 'relative',
        paddingRight: openDrawer && !isMob ? '73px' : '0px',
      }}
    >
      <div className={classes.mobMenu}>
        <IconButton onClick={handleDrawerMobOpen}>
          <Icon
            path={mdiMenu}
            className={classes.icon}
            size={1}
            color="primary"
          />
        </IconButton>
      </div>
      <Backdrop
        open={isMob && openDrawer}
        onClick={handleDrawerClose}
        style={{ zIndex: 100 }}
      />
      <div
        style={{ position: openDrawer ? 'fixed' : 'relative', zIndex: 100 }}
        className={clsx(classes.sidebar, {
          [classes.sidebarOpenMob]: isMob && openDrawer,
          [classes.sidebarCloseMob]: isMob && !openDrawer,
        })}
      >
        <Drawer
          open={openDrawer}
          onMouseOver={handleDrawerOver}
          onMouseLeave={handleDrawerLeave}
          variant={'permanent'}
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: openDrawer,
            [classes.drawerClose]: !openDrawer,
          })}
          classes={{
            paper: clsx({
              [classes.drawerOpen]: openDrawer,
              [classes.drawerClose]: !openDrawer,
            }),
          }}
        >
          <div
            className={clsx(classes.toolbar, {
              [classes.toolbarOpen]: openDrawer,
              [classes.toolbarClose]: !openDrawer,
            })}
          >
            {!openDrawer ? (
              <Avatar
                variant="rounded"
                className={clsx(classes.avatar, {
                  [classes.avatarSmall]: !openDrawer,
                  [classes.avatarMob]: isMob,
                })}
                src="/logo2.png"
                sizes="sm"
              />
            ) : (
              <Avatar
                style={{
                  paddingTop: '24px', paddingBottom: '24px',
                  paddingLeft: '64px', paddingRight: '16px'
                }}
                variant="rounded"
                className={clsx(classes.avatar, {
                  [classes.avatarLarg]: openDrawer,
                  [classes.avatarMob]: isMob,
                })}
                src="/ronad-logo.svg"
                sizes='50vw'
              />
            )}
          </div>

          {links}

          <div className={classes.toggleHolder}></div>
          <div className={classes.logoutProfileContainer}>
            {/* <div className={classes.userInfoContainer}>
              <div className={classes.userInfoBox}>
                <Avatar className={classes.userAvatar} />
                <div className={classes.username}>
                  <Link to="profile" style={{ color: "#000", textDecoration: "none" }}>
                    {openDrawer && authContext.user.name}
                  </Link>
                </div>

              </div>

            </div> */}


            {/* Logout */}
            <div className={classes.userInfoContainer}>
              <div className={classes.logoutBar}>
                <img src="/logOut.svg" className={classes.logoutIcon} />


                <div className={classes.username}>
                  <Link to="login" style={{ color: "#000", textDecoration: "none" }}>
                    {openDrawer && <span>خروج</span>}
                  </Link>
                </div>

              </div>

            </div>
          </div>


          <Dialog
            fullWidth
            open={openConfirmLogoutBox}
            onClose={() => setOpenConfirmLogoutBox(false)}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogTitle>{' از خروج از پنل ادمین اطمینان دارید؟'}</DialogTitle>
            <DialogActions>
              <Button
                autoFocus
                onClick={() => setOpenConfirmLogoutBox(false)}
                color="primary"
              >
                {'خیر'}
              </Button>
              <Button
                onClick={handleOpenConfirmLogoutBox}
                variant="contained"
                color="primary"
                data-cy="logout-click"
              >
                {'بله'}
              </Button>
            </DialogActions>
          </Dialog>
        </Drawer>
      </div>
    </div>
  );
}
